/**
 * Chip — лёгкая «таблетка» для отображения тегов / выбора из списка.
 *
 * Два режима:
 *   - selected={undefined} — display-only (нон-interactive, без курсора).
 *   - selected={boolean}   — selectable; tap → onPress, активный/пассивный
 *                            вид через тон фона/каёмки/текста.
 *
 * Дизайн-токены через ThemeContext, ничего hard-coded. Размер и spacing
 * подогнаны под существующие фильтр-чипы PracticeRoomsScreen, чтобы вновь
 * созданный AMB-263-кабинет не выглядел как пришелец.
 */

import React from 'react';
import { TouchableOpacity, View, Platform, type ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Typography } from './Typography';
import { useTheme } from '../../context/ThemeContext';

export interface ChipProps {
  label: string;
  /** undefined → display-only; boolean → selectable. */
  selected?: boolean;
  onPress?: () => void;
  /** Selectable, but currently not choosable (gasится): dimmed, non-pressable. */
  disabled?: boolean;
  /** Small icon area before the label (optional). */
  prefix?: React.ReactNode;
  accessibilityLabel?: string;
  testID?: string;
  style?: ViewStyle;
}

export const Chip: React.FC<ChipProps> = ({ label, selected, onPress, disabled = false, prefix, accessibilityLabel, testID, style }) => {
  const { isDark, colors } = useTheme();
  const interactive = typeof selected === 'boolean' && !disabled;
  const isOn = selected === true;

  const bg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,248,243,0.72)';
  const border = isOn
    ? '#E98245'
    : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,20,25,0.08)');
  const fg = isOn ? '#FFFFFF' : colors.textPrimary;

  const content = (
    <>
      {prefix}
      <Typography
        style={{
          color: fg,
          fontSize: 13,
          fontWeight: '600',
          letterSpacing: 0.1,
        }}
      >
        {label}
      </Typography>
    </>
  );

  const chipStyle = [
    {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: 6,
      paddingHorizontal: 12,
      paddingVertical: 7,
      borderRadius: 999,
      backgroundColor: isOn ? undefined : bg,
      borderWidth: 1,
      borderColor: border,
      opacity: disabled ? 0.4 : 1,
      ...(isOn && Platform.OS === 'web' ? ({ boxShadow: colors.ctaShadow } as any) : {}),
    },
    style,
  ];

  const inner = isOn ? (
    <LinearGradient
      colors={[colors.ctaStart, colors.ctaEnd]}
      start={{ x: 0, y: 0.5 }}
      end={{ x: 1, y: 0.5 }}
      style={chipStyle}
    >
      {content}
    </LinearGradient>
  ) : (
    <View
      style={[
        ...chipStyle,
      ]}
    >
      {content}
    </View>
  );

  if (!interactive) return inner;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.85}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      accessibilityState={{ selected: isOn }}
      testID={testID}
      style={Platform.OS === 'web' ? ({ cursor: 'pointer', outlineStyle: 'none' } as any) : undefined}
    >
      {inner}
    </TouchableOpacity>
  );
};

export interface ChipListProps {
  items: { value: string; label: string }[];
  /** When set, items are selectable and `selected` reflects the active set. */
  selected?: string[];
  onToggle?: (value: string) => void;
  /**
   * Values that are currently not choosable (e.g. an interval outside the
   * selected time-of-day, CORE-911). Rendered dimmed and non-pressable. A
   * value that is already selected is never disabled — иначе пользователь не
   * смог бы снять старую (в т.ч. невозможную) комбинацию.
   */
  disabledValues?: string[];
  emptyText?: string;
  /** Stable prefix for E2E selectors of interactive chips. */
  testIDPrefix?: string;
}

/**
 * ChipList — обёртка с автопереносом и пустым состоянием. В режиме
 * `selected` undefined рендерит чипы read-only (для блоков на «Профиль»),
 * иначе превращает каждый чип в toggle (для «Настройки»).
 */
export const ChipList: React.FC<ChipListProps> = ({ items, selected, onToggle, disabledValues, emptyText, testIDPrefix }) => {
  const { colors } = useTheme();
  const interactive = Array.isArray(selected);

  if (!interactive && items.length === 0) {
    return (
      <Typography style={{ color: colors.textMuted, fontSize: 13, fontStyle: 'italic' }}>
        {emptyText ?? '—'}
      </Typography>
    );
  }

  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
      {items.map(item => {
        const isSelected = interactive ? (selected as string[]).includes(item.value) : undefined;
        const isDisabled = interactive && !isSelected && !!disabledValues?.includes(item.value);
        return (
          <Chip
            key={item.value}
            label={item.label}
            selected={isSelected}
            disabled={isDisabled}
            onPress={interactive ? () => onToggle?.(item.value) : undefined}
            testID={testIDPrefix ? `${testIDPrefix}-${item.value}` : undefined}
          />
        );
      })}
    </View>
  );
};

export default Chip;
